import type { Messages } from "@/i18n/locales/en";
import { legalMessagesEt } from "@/i18n/locales/legalEtData";
import { pagesMessagesEt } from "@/i18n/locales/pagesEtData";

export const etMessages = {
  meta: {
    description:
      "WasOK — skanni tooteid, salvesta hinnanguid ja märkmeid, pea meeles, mis oli ostmist väärt.",
    title: "WasOK — Pea meeles, mis oli ostmist väärt",
  },
  header: {
    navFeatures: "Funktsioonid",
    navHowItWorks: "Kuidas see töötab",
    navPrivacy: "Privaatsus",
    navUseCases: "Kasutusjuhud",
    download: "Laadi alla",
    openMenu: "Ava menüü",
    closeMenu: "Sulge menüü",
    languageAria: "Keel",
  },
  hero: {
    badge: "📱 Nüüd saadaval Androidis",
    title: "Pea meeles, mis oli ostmist väärt.",
    subtitle:
      "Skanni tooteid, salvesta hinnanguid ja märkmeid — ning tea kohe, kas toode oli tegelikult hea.",
    googlePlay: "Laadi alla Google Playst",
    watchDemo: "Vaata demot",
    privacyLine: "Kõik andmed jäävad teie seadmesse.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skanni, et meeles pidada",
    snackCard: "Maitsvalt!",
    coffeeCard: "Liiga mõru",
    scrollHint: "Kerige avastamiseks",
  },
  problems: {
    heading: "Tundub tuttav?",
    subheading:
      "Oleme kõik seal olnud — seisnud riiuli ees ja püüdnud meenutada, kas meile viimati meeldis.",
    notedAgo: "✓ Märkisite selle 3 kuud tagasi",
    items: [
      {
        question: "Milline šokolaad mulle tegelikult meeldis?",
        note: "Täiuslik magusus",
      },
      {
        question: "Miks ma lõpetasin selle šampooni ostmise?",
        note: "Kuivatas juuksed ära",
      },
      {
        question: "Kas see kohv oli hea või pettumus?",
        note: "Liiga mõru",
      },
    ],
  },
  howItWorks: {
    heading: "Kuidas see töötab",
    subheading:
      "Neli lihtsat sammu, et te oma tootearvamusi enam kunagi ei unustaks.",
    stepPrefix: "Samm",
    steps: [
      {
        title: "Skanni mis tahes toote vöötkood",
        description:
          "Kiire ja lihtne skannimine kaameraga. Töötab iga vöötkoodiga.",
      },
      {
        title: "Salvesta hinnangud, kommentaarid ja fotod",
        description:
          "Hinda tooteid, lisa märkmeid, fotosid, hindu ja asukohti.",
      },
      {
        title: "Pea oma kogemus kohe meeles",
        description:
          "Skanni sama toodet hiljem ja näe oma varasemat hinnangut ning märkmeid.",
      },
      {
        title: "Loo nutikaid ostunimekirju",
        description:
          "Koosta ostunimekirju toodetest, mida juba usaldad ja armastad.",
      },
    ],
  },
  privacy: {
    heading: "Teie ostuajalugu jääb teie omaks.",
    body: "WasOK aitab meeles pidada tooteid, mida olete proovinud, samal ajal kui hinnangud, märkmed, fotod ja ostunimekirjad salvestatakse kohalikult teie seadmesse.",
    trustBanner:
      "💚 Ehitatud teie ostuajaloo ümber — mitte teie andmete kogumise ümber.",
    bullets: [
      "Kontot pole vaja",
      "Pilvesünkroniseerimist pole vaja",
      "Kohalik salvestus",
      "Kasutaja kontrollitud varukoopiad",
      "Valikuline vöötkoodiotsing tooteinfo jaoks.",
      "Reklaamitoega Google AdMob",
    ],
  },
  features: {
    heading: "Kõik, mida vajate",
    subheading:
      "Võimsad funktsioonid, mis teevad toodete jälgimise lihtsaks ja privaatseks.",
    items: [
      {
        title: "Vöötkoodiskanner",
        description: "Kiire ja täpne vöötkoodi skannimine mis tahes tootele.",
      },
      {
        title: "Tootehinnangud",
        description: "Hinda tooteid 1–5 tärniga, et kvaliteeti meeles pidada.",
      },
      {
        title: "Tootefotod",
        description: "Lisa fotosid pakendi või detailide meelespidamiseks.",
      },
      {
        title: "Kategooriad",
        description: "Korralda tooted oma kategooriatesse.",
      },
      {
        title: "Poodide jälgimine",
        description: "Pea meeles, kust iga toode ostsite.",
      },
      {
        title: "Hindade logimine",
        description: "Jälgi hindu, et leida parimad pakkumised.",
      },
      {
        title: "Ostunimekirjad",
        description: "Koosta nimekirju lemmiktoodetest.",
      },
      {
        title: "Võrguühenduseta juurdepääs",
        description: "Töötab ideaalselt ilma internetita.",
      },
      {
        title: "Kohalik salvestus",
        description: "Kõik andmed salvestatakse turvaliselt teie seadmesse.",
      },
    ],
  },
  useCases: {
    heading: "Sobib igasse riiulisse",
    subheading:
      "Olgu tegemist toidu, ilu või majapidamisega — WasOK aitab meeles pidada.",
    cards: [
      {
        emoji: "🍫",
        title: "Snäkid ja maiustused",
        description: "Pea meeles, millised kommid olid tegelikult head.",
      },
      {
        emoji: "☕",
        title: "Kohv ja joogid",
        description: "Jälgi kohvisid, mida tasub uuesti osta.",
      },
      {
        emoji: "💄",
        title: "Kosmeetika ja nahahooldus",
        description: "Hoia märkmeid naha reaktsioonide kohta.",
      },
      {
        emoji: "🧹",
        title: "Majapidamine",
        description: "Väldi toodete uuesti ostmist, mis petasid.",
      },
    ],
  },
  screenshots: {
    heading: "Ilus ja intuitiivne",
    subheading:
      "Läbimõeldud liides, mis teeb toodete jälgimise nauditavaks.",
    items: [
      { title: "Skanner" },
      { title: "Toote detail" },
      { title: "Hinnangu sisestus" },
      { title: "Otsing ja ajalugu" },
      { title: "Ostunimekiri" },
    ],
  },
  cta: {
    heading: "Ostumälu päriseluks.",
    body: "Lõpetage unustamine, millised tooted olid ostmist väärt. Alustage targemat ostmist juba täna.",
    googlePlay: "Laadi alla Google Playst",
    scanQr: "Skanni allalaadimiseks",
    tagline: "Privaatne · Võrguühenduseta esmalt · Lihtne",
  },
  footer: {
    tagline: "Pea meeles, mis oli ostmist väärt.",
    linksHeading: "Lingid",
    privacyPolicy: "Privaatsuspoliitika",
    terms: "Teenusetingimused",
    contact: "Kontakt",
    linkFaq: "KKK",
    linkLicenses: "Avatud lähtekoodi litsentsid",
    linkAdvertising: "Reklaam ja monetiseerimine",
    downloadHeading: "Laadi alla",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Kõik õigused kaitstud. Tehtud 💚-ga targema ostmise jaoks.",
  },
  legal: legalMessagesEt,
  faq: pagesMessagesEt.faq,
  support: pagesMessagesEt.support,
  advertising: pagesMessagesEt.advertising,
} satisfies Messages;
